/**
 * Safe file upload validation, preview, and removal for storefront forms.
 * The server remains authoritative; this client policy gives customers an
 * immediate, consistent explanation before a request is submitted.
 */
(function () {
  'use strict';

  var MAX_FILES = 5;
  var MAX_FILE_BYTES = 10 * 1024 * 1024;
  var MAX_TOTAL_BYTES = 25 * 1024 * 1024;
  var ALLOWED_EXTENSIONS = Object.freeze(['png', 'jpg', 'jpeg', 'webp', 'gif']);
  var ALLOWED_MIME_TYPES = Object.freeze([
    'image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'
  ]);
  var MIME_TYPES_BY_SIGNATURE = Object.freeze({
    png: Object.freeze(['image/png']),
    jpeg: Object.freeze(['image/jpeg', 'image/jpg']),
    webp: Object.freeze(['image/webp']),
    gif: Object.freeze(['image/gif'])
  });
  var uploadStates = new WeakMap();

  function isFrench() {
    return (document.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;
  }

  function copy(key) {
    var messages = {
      checking: { en: 'Checking selected files…', fr: 'Vérification des fichiers…' },
      count: { en: 'Choose no more than 5 files.', fr: 'Choisissez au maximum 5 fichiers.' },
      fileSize: { en: 'Each file must be 10 MB or smaller.', fr: 'Chaque fichier doit avoir une taille maximale de 10 Mo.' },
      totalSize: { en: 'The combined file size must be 25 MB or smaller.', fr: 'La taille totale des fichiers doit être de 25 Mo ou moins.' },
      type: { en: 'Use a verified PNG, JPG, WebP or GIF file.', fr: 'Utilisez un fichier PNG, JPG, WebP ou GIF vérifié.' },
      read: { en: 'We could not verify that file. Choose another PNG, JPG, WebP or GIF.', fr: 'Nous n’avons pas pu vérifier ce fichier. Choisissez un autre fichier PNG, JPG, WebP ou GIF.' },
      remove: { en: 'Remove', fr: 'Retirer' },
      manage: {
        en: 'This browser cannot safely remove one file. Choose the files again to change this selection.',
        fr: 'Ce navigateur ne peut pas retirer un seul fichier de façon sûre. Sélectionnez de nouveau les fichiers pour modifier ce choix.'
      }
    };
    var message = messages[key] || messages.type;
    return isFrench() ? message.fr : message.en;
  }

  function getExtension(filename) {
    var match = String(filename || '').toLowerCase().match(/\.([a-z0-9]+)$/);
    return match ? match[1] : '';
  }

  function normalizedExpectedType(extension) {
    return extension === 'jpg' ? 'jpeg' : extension;
  }

  function readHeader(file) {
    var blob = file.slice(0, 16);
    if (typeof blob.arrayBuffer === 'function') {
      return blob.arrayBuffer().then(function (buffer) { return new Uint8Array(buffer); });
    }
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () { resolve(new Uint8Array(reader.result)); };
      reader.onerror = function () { reject(reader.error || new Error('File read failed')); };
      reader.readAsArrayBuffer(blob);
    });
  }

  function ascii(bytes, start, length) {
    return Array.prototype.slice.call(bytes, start, start + length).map(function (byte) {
      return String.fromCharCode(byte);
    }).join('');
  }

  function detectFileType(bytes) {
    if (bytes.length >= 8 && bytes[0] === 0x89 && ascii(bytes, 1, 3) === 'PNG' && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a) return 'png';
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
    if (bytes.length >= 6 && (ascii(bytes, 0, 6) === 'GIF87a' || ascii(bytes, 0, 6) === 'GIF89a')) return 'gif';
    if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') return 'webp';
    return '';
  }

  async function validateFiles(files) {
    var selected = Array.prototype.slice.call(files || []);
    if (selected.length > MAX_FILES) return { valid: false, message: copy('count') };
    var total = selected.reduce(function (sum, file) { return sum + Number(file.size || 0); }, 0);
    if (selected.some(function (file) { return Number(file.size || 0) > MAX_FILE_BYTES; })) return { valid: false, message: copy('fileSize') };
    if (total > MAX_TOTAL_BYTES) return { valid: false, message: copy('totalSize') };

    for (var index = 0; index < selected.length; index += 1) {
      var file = selected[index];
      var extension = getExtension(file.name);
      if (ALLOWED_EXTENSIONS.indexOf(extension) === -1) return { valid: false, message: copy('type') };
      var suppliedMime = String(file.type || '').toLowerCase();
      if (!suppliedMime || ALLOWED_MIME_TYPES.indexOf(suppliedMime) === -1) return { valid: false, message: copy('type') };
      try {
        var detected = detectFileType(await readHeader(file));
        if (!detected || detected !== normalizedExpectedType(extension)) return { valid: false, message: copy('type') };
        if (!MIME_TYPES_BY_SIGNATURE[detected] || MIME_TYPES_BY_SIGNATURE[detected].indexOf(suppliedMime) === -1) {
          return { valid: false, message: copy('type') };
        }
      } catch (_error) {
        return { valid: false, message: copy('read') };
      }
    }
    return { valid: true, message: '' };
  }

  function formatFileSize(bytes) {
    if (!bytes) return '0 Bytes';
    var units = ['Bytes', 'KB', 'MB'];
    var unit = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return Math.round(bytes / Math.pow(1024, unit) * 100) / 100 + ' ' + units[unit];
  }

  function truncateFileName(name, maxLength) {
    if (name.length <= maxLength) return name;
    var dot = name.lastIndexOf('.');
    var extension = dot >= 0 ? name.slice(dot) : '';
    return name.slice(0, Math.max(1, maxLength - extension.length - 3)) + '...' + extension;
  }

  function ensureStatus(input) {
    var status = input.parentElement && input.parentElement.querySelector('[data-ens-upload-error]');
    if (status) return status;
    status = document.createElement('small');
    status.dataset.ensUploadError = '';
    status.className = 'file-upload-error';
    status.setAttribute('role', 'alert');
    status.setAttribute('aria-live', 'polite');
    if (input.parentElement) input.parentElement.insertBefore(status, input.nextSibling);
    return status;
  }

  function setStatus(input, message) {
    input.setCustomValidity(message || '');
    var status = ensureStatus(input);
    status.textContent = message || '';
    status.hidden = !message;
  }

  function assignFiles(input, files) {
    if (typeof DataTransfer === 'undefined') return false;
    try {
      var transfer = new DataTransfer();
      files.forEach(function (file) { transfer.items.add(file); });
      input.files = transfer.files;
      var assigned = Array.prototype.slice.call(input.files || []);
      return assigned.length === files.length && assigned.every(function (file, index) {
        var expected = files[index];
        return file.name === expected.name && file.size === expected.size && file.lastModified === expected.lastModified;
      });
    } catch (_error) {
      return false;
    }
  }

  function clearChildren(container) {
    while (container.firstChild) container.removeChild(container.firstChild);
  }

  function renderFilePreview(container, files, onRemove, canRemove) {
    clearChildren(container);
    if (!files.length) return;
    var list = document.createElement('div');
    list.className = 'file-preview-list';
    files.forEach(function (file, index) {
      var item = document.createElement('div');
      item.className = 'file-preview-item';
      var info = document.createElement('div');
      info.className = 'file-preview-info';
      var filename = document.createElement('div');
      filename.className = 'file-preview-name';
      filename.title = file.name;
      filename.textContent = truncateFileName(file.name, 40);
      var size = document.createElement('div');
      size.className = 'file-preview-size';
      size.textContent = formatFileSize(file.size);
      info.appendChild(filename);
      info.appendChild(size);
      item.appendChild(info);
      if (canRemove) {
        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'file-preview-remove';
        remove.textContent = copy('remove');
        remove.setAttribute('aria-label', copy('remove') + ' ' + file.name);
        remove.addEventListener('click', function () { onRemove(index); });
        item.appendChild(remove);
      }
      list.appendChild(item);
    });
    container.appendChild(list);
  }

  function initializeFileInput(input) {
    // The designer owns its JSON imports and room/artwork inputs. Applying this
    // storefront image-only policy clears those files before its handlers run.
    if (input.closest('#ens-reference-designer')) return;
    if (input.dataset.uploadInitialized === 'true') return;
    input.dataset.uploadInitialized = 'true';
    var selectedFiles = [];
    var validationGeneration = 0;
    var canSafelyAssignFiles = false;
    var preview = document.createElement('div');
    preview.className = 'file-preview-container';
    if (input.parentElement) input.parentElement.insertBefore(preview, input.nextSibling);
    ensureStatus(input);

    var state = { files: selectedFiles };
    uploadStates.set(input, state);

    function setSelectedFiles(files) {
      selectedFiles = files.slice();
      state.files = selectedFiles;
    }

    function filesAcrossForm(ownFiles) {
      var form = input.closest('form');
      if (!form) return ownFiles.slice();
      var allFiles = [];
      form.querySelectorAll('input[type="file"]').forEach(function (fileInput) {
        if (fileInput === input) allFiles = allFiles.concat(ownFiles);
        else {
          var otherState = uploadStates.get(fileInput);
          allFiles = allFiles.concat(otherState ? otherState.files : Array.prototype.slice.call(fileInput.files || []));
        }
      });
      return allFiles;
    }

    function draw() {
      renderFilePreview(preview, selectedFiles, function (index) {
        var nextFiles = selectedFiles.filter(function (_file, fileIndex) { return fileIndex !== index; });
        if (!assignFiles(input, nextFiles)) {
          setStatus(input, copy('manage'));
          draw();
          return;
        }
        setSelectedFiles(nextFiles);
        setStatus(input, '');
        draw();
      }, canSafelyAssignFiles);
    }

    input.addEventListener('change', async function () {
      validationGeneration += 1;
      var currentGeneration = validationGeneration;
      var incoming = Array.prototype.slice.call(input.files || []);
      var candidates = input.multiple ? selectedFiles.slice() : [];
      incoming.forEach(function (file) {
        var duplicate = candidates.some(function (existing) {
          return existing.name === file.name && existing.size === file.size && existing.lastModified === file.lastModified;
        });
        if (!duplicate) candidates.push(file);
      });

      input.dataset.uploadValidating = 'true';
      setStatus(input, copy('checking'));
      var result = await validateFiles(filesAcrossForm(candidates));
      if (currentGeneration !== validationGeneration) return;
      delete input.dataset.uploadValidating;
      if (!result.valid) {
        if (!assignFiles(input, selectedFiles)) {
          input.value = '';
          setSelectedFiles([]);
          canSafelyAssignFiles = false;
          draw();
        }
        setStatus(input, result.message);
        return;
      }
      var assignedCandidates = assignFiles(input, candidates);
      if (assignedCandidates) {
        setSelectedFiles(candidates);
        canSafelyAssignFiles = true;
      } else {
        // The browser's current FileList is the only source of truth when
        // programmatic FileList assignment is unsupported or rejected.
        setSelectedFiles(incoming);
        canSafelyAssignFiles = false;
      }
      setStatus(input, '');
      draw();
    });

    var form = input.closest('form');
    if (form) {
      form.addEventListener('reset', function () {
        validationGeneration += 1;
        delete input.dataset.uploadValidating;
        setSelectedFiles([]);
        canSafelyAssignFiles = false;
        setStatus(input, '');
        clearChildren(preview);
      });
    }
  }

  function initializeUploads(root) {
    (root || document).querySelectorAll('input[type="file"]').forEach(initializeFileInput);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initializeUploads(document); });
  else initializeUploads(document);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || typeof form.querySelector !== 'function') return;
    var pending = form.querySelector('input[type="file"][data-upload-validating="true"]');
    var invalid = form.querySelector('input[type="file"]:invalid');
    var blocked = pending || invalid;
    if (!blocked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    blocked.reportValidity();
    blocked.focus();
  }, true);

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('input[type="file"]')) initializeFileInput(node);
        else if (node.querySelectorAll) initializeUploads(node);
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });

  window.EasyNeonUploadPolicy = Object.freeze({
    validateFiles: validateFiles,
    detectFileType: detectFileType,
    assignFiles: assignFiles,
    limits: Object.freeze({ maxFiles: MAX_FILES, maxFileBytes: MAX_FILE_BYTES, maxTotalBytes: MAX_TOTAL_BYTES })
  });
})();
